import { useEffect, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { useSearchParams, useLocation } from "react-router-dom";
import { useNavigate, guardTab } from "@/hooks/useDeskNavigate";
import {
  Plus, FileDown, FileSpreadsheet, ListTodo, Trash2,
  List, CalendarClock, Clock, Loader, AlertTriangle, Eye, EyeOff, RotateCw, Check, LockIcon as LockIcon, UserCheck, BellRing,
  UserCircle2, Globe2, LayoutGrid, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { OutlookTabs } from "@/components/common/OutlookTabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import FiltersBar from "@/components/tasks/FiltersBar";
import TaskCreateDialog from "@/components/tasks/TaskCreateDialog";
import TaskListView from "@/components/tasks/TaskListView";
import TaskKanbanView from "@/components/tasks/TaskKanbanView";
import TaskCalendarView from "@/components/tasks/TaskCalendarView";
import api, { dedupedGet } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { useViewScope } from "@/context/ViewScopeContext";
import { MODULE_COLORS } from "@/lib/moduleColors";
import HeaderProfileGroup from "@/components/TopBar";
import HeaderPill from "@/components/ui/HeaderPill";
import ScopeSwitch from "@/components/ui/ScopeSwitch";
import KpiSelectedBadge from "@/components/ui/KpiSelectedBadge";
import useSearchShortcut from "@/hooks/useSearchShortcut";
import useDebouncedValue from "@/hooks/useDebouncedValue";
import useFiltersSnapshot from "@/hooks/useFiltersSnapshot";
import useScrollRestore, { clearScrollMemory } from "@/hooks/useScrollRestore";

const PAGE_SIZE = 50;
const DEFAULT_FILTERS = {
  search: "", reference: "", status: "", priority: "",
  project_id: "", created_by: "", assignee_id: "",
  date_from: "", date_to: "",
  scope: "", auto_only: "", page: 1,
};

export default function Tasks() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canCreateTask = can("taches", "create");
  const isAdminLevel = user?.role === "admin" || user?.is_first_admin;
  // "Personal" vs "Global" KPI band scope (spec 2026-02-18 sexies).
  // Managers + regular users are hard-locked on personal — the backend
  // enforces it too. Admins & the "Premier Admin" default to personal but
  // can toggle to global to see the whole team's workload.
  // Spec 2026-07-18 (batch 5 points) #4 — le toggle est porté par un
  // contexte partagé qui persiste entre Tâches et Documents pour toute la
  // durée de la session utilisateur.
  const { viewScope, setViewScope } = useViewScope();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Spec rev82 (2026-08-02) — raccourci "/" pour focus la barre de recherche
  // du module Tâches. L'input est rendu dans <FiltersBar> avec le testid
  // "filter-search" ; on le récupère par querySelector pour ne pas devoir
  // exposer une ref à travers le composant.
  useSearchShortcut(() => document.querySelector('[data-testid="filter-search"]'));

  // View is persisted in the URL (`?view=list|kanban|calendar`) so that
  // navigating away to a task detail and hitting "Retour" restores the
  // exact tab the user came from.
  const [view, setView] = useState(() => {
    const v = sp.get("view");
    return v === "kanban" || v === "calendar" ? v : "list";
  });
  // Sync view state → URL. Using `navigate` + a hand-built query string so
  // the update goes through the router directly (dodges any batching quirks
  // we saw with the functional form of `setSearchParams` in RRv7).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (view === "list") params.delete("view");
    else params.set("view", view);
    const nextSearch = params.toString();
    const target = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
    if (target !== `${location.pathname}${location.search}`) {
      navigate(target, { replace: true });
    }
    // Spec iter156 — synchro URL déclenchée UNIQUEMENT par le changement
    // de ``view`` ; inclure location/navigate provoquerait une boucle
    // (navigate change location, qui re-déclencherait l'effet).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Calendar cursor lives in the URL too (`?month=YYYY-MM`) — restored on
  // back-navigation so the user lands on the same month they were browsing.
  const monthFromUrl = sp.get("month");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, late: 0, in_progress: 0, completed: 0 });
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  // Spec iter102b (2026-08) — persistance limitée à la SEULE sélection KPI
  // (filters.status + filters.scope). La recherche, le tri, les autres
  // filtres, et les toggles ne sont PAS persistés — retour aux défauts
  // au retour depuis une fiche, comme le comportement historique.
  const initialSnap = (() => {
    try {
      const raw = sessionStorage.getItem("tasks:filters:v1");
      if (!raw) return null;
      const s = JSON.parse(raw);
      if (!s?.saved_at || Date.now() - s.saved_at > 5 * 60 * 1000) return null;
      return s;
    } catch { return null; }
  })();
  // iter130 — restauration COMPLÈTE des filtres (recherche, statut, priorité, projet, personnes, dates, scope, page).
  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    ...(initialSnap?.filters || {}),
    status: initialSnap?.filters?.status ?? initialSnap?.status ?? "",
    scope:  initialSnap?.filters?.scope ?? initialSnap?.scope  ?? "",
  });
  // Spec rev163 (2026-02) — debounce 250 ms sur les champs de recherche
  // (search + reference issus de l'input unifié) pour éviter un fetch
  // par frappe et alimenter le liseré animé dans FiltersBar.
  const debouncedSearch = useDebouncedValue(filters.search, 250);
  const debouncedReference = useDebouncedValue(filters.reference, 250);
  const [openCreate, setOpenCreate] = useState(false);
  const [createPrefillDate, setCreatePrefillDate] = useState("");
  const [loading, setLoading] = useState(false);

  // "Afficher les clôturées" toggle — persisted in the URL (`?completed=1`)
  // so a bookmark / round-trip via Retour preserves the choice. Default is
  // OFF: closed tasks are hidden from the List view and from the KPIs.
  const [showCompleted, setShowCompleted] = useState(() => sp.get("completed") === "1" || localStorage.getItem(`ledesk_show_completed:${user?.id || ""}`) === "1");
  useEffect(() => { localStorage.setItem(`ledesk_show_completed:${user?.id || ""}`, showCompleted ? "1" : "0"); }, [showCompleted, user?.id]);
  // Spec rev92 (2026-08-02) — sortKey / sortDir sont désormais gérés ici
  // (au lieu de dans <TaskListView>) pour que le toggle « Activité récente »
  // puisse être fusionné avec « Afficher les clôturées » sur la même barre.
  const [sortKey, setSortKey] = useState(initialSnap?.sortKey || "created_at");
  const [sortDir, setSortDir] = useState(initialSnap?.sortDir || "desc");

  // iter130 — auto-save de TOUT l'état de liste (filtres + tri) pour la session.
  useFiltersSnapshot("tasks:filters:v1", {
    status: filters.status,
    scope: filters.scope,
    filters, sortKey, sortDir,
  });
  useScrollRestore("tasks:filters:v1", !loading && items.length > 0, "[data-testid=task-list-scroll]");
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (showCompleted) params.set("completed", "1");
    else params.delete("completed");
    const nextSearch = params.toString();
    const target = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
    if (target !== `${location.pathname}${location.search}`) {
      navigate(target, { replace: true });
    }
  }, [showCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from Dashboard: /taches?new=1 opens the create dialog.
  useEffect(() => {
    if (sp.get("new") === "1") {
      setOpenCreate(true);
      sp.delete("new");
      setSp(sp, { replace: true });
    }
  }, [sp, setSp]);

  // Fetch reference data once
  useEffect(() => {
    Promise.all([api.get("/projects"), api.get("/users/directory")])
      .then(([p, u]) => { setProjects(p.data); setUsers(u.data); })
      .catch(() => toast.error("Impossible de charger les données de référence"));
  }, []);

  // Spec 2026-02-XX rev10 — infinite scroll : accumulateur + sentinel.
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Fetch tasks whenever filters change (or after create). Charge page 1
  // en mode « replace » ; les pages suivantes sont ajoutées en append via
  // ``loadMoreTasks`` (déclenché par l'IntersectionObserver dans TaskListView).
  // Spec iter191 (2026-02-27) — Point 1 : `silent` flag pour éviter
  // le flash « Chargement… » sur refetch en arrière-plan (retour focus,
  // polling…). La liste courante reste visible pendant le refresh.
  const reloadRef = useRef(null);
  const reload = async ({ append = false, targetPage = 1, silent = false } = {}) => {
    if (!silent) {
      if (append) setLoadingMore(true); else setLoading(true);
    }
    try {
      const params = { page_size: PAGE_SIZE, page: targetPage };
      // Spec rev163 (2026-02) — les 2 champs debouncés remplacent leur
      // équivalent dans `filters` pour les fetches serveur.
      const effectiveFilters = { ...filters, search: debouncedSearch, reference: debouncedReference };
      [
        "search", "reference", "status", "priority",
        "project_id", "created_by", "assignee_id",
        "date_from", "date_to", "scope", "auto_only",
      ].forEach((k) => {
        if (effectiveFilters[k]) params[k] = effectiveFilters[k];
      });
      if (showCompleted) params.include_completed = "true";
      // Spec iter266 point 5 (2026-02) — le KPI « Clôturée » filtre
      // exclusivement sur ce statut, il faut aussi lever le gate backend
      // (`include_completed`) sinon la liste revient vide. Idem pour le
      // filtre colonne « Statut = closed » — même contrainte.
      if (filters.status === "closed") params.include_completed = "true";
      // Personal-view scope (spec 2026-02-18 sexies) — force `assignee_id`
      // to the current user unless the user has opted into the global view.
      // Non-admins are always locked on personal (server also enforces).
      if (viewScope === "mine" && user?.id && !filters.assignee_id) {
        params.assignee_id = user.id;
      } else if (viewScope.startsWith("user:") && !filters.assignee_id) {
        params.assignee_id = viewScope.slice(5);
      }
      const { data } = await dedupedGet("/tasks", { params });
      const newItems = data.items || [];
      const t = data.total || 0;
      setTotal(t);
      setStats(data.stats || { total: 0, late: 0, in_progress: 0, completed: 0 });
      setItems((prev) => append ? [...prev, ...newItems] : newItems);
      const nextCount = (append ? items.length : 0) + newItems.length;
      setHasMore(nextCount < t);
      // Mise à jour tracker de page pour la prochaine passe append.
      setFilters((f) => f.page === targetPage ? f : { ...f, page: targetPage });
    } catch (err) {
      toast.error("Chargement des tâches impossible", { description: err.response?.data?.detail || "" });
    } finally {
      if (!silent) {
        if (append) setLoadingMore(false); else setLoading(false);
      }
    }
  };

  reloadRef.current = reload;
  const loadMoreTasks = () => {
    if (loading || loadingMore || !hasMore) return;
    reload({ append: true, targetPage: filters.page + 1 });
  };

  // Spec rev10 : quand un filtre change (hors ``page``), on refetch la page 1.
  useEffect(() => {
    reload({ append: false, targetPage: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch, debouncedReference, filters.status, filters.priority,
    filters.project_id, filters.created_by, filters.assignee_id,
    filters.date_from, filters.date_to, filters.scope,
    showCompleted, viewScope, user?.id,
  ]);

  // Spec iter191 (2026-02-27) — Point 3 : polling léger 20s (silent
  // refetch quand l'onglet est visible) + refetch au retour de focus.
  useEffect(() => {
    // iter205 (C12) — toujours la version COURANTE de `reload` (filtres + recherche à jour), jamais la closure du 1er rendu
    const softReload = () => { if (document.activeElement?.closest?.("[data-testid='tasks-search-input'], input[type='search']")) return; reloadRef.current?.({ append: false, targetPage: 1, silent: true }); };
    const onVisibility = () => {
      if (document.visibilityState === "visible") softReload();
    };
    const poll = setInterval(() => {
      if (document.visibilityState === "visible") softReload();
    }, 20000);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportFile = async (fmt) => {
    try {
      const resp = await api.get(`/tasks/export/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = fmt === "xlsx" ? "taches_le_desk.xlsx" : "taches_le_desk.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error("Export impossible", { description: err.response?.data?.detail || "" });
    }
  };

  return (
    <div
      className="flex flex-col gap-4 min-h-0"
      style={{ height: "calc(100vh - 80px)" }}
      data-testid="tasks-page"
    >
      {/* --- Fused header band: icon + title + 6 mini-KPIs + actions ------- */}
      <FusedTasksHeader
        total={total}
        stats={stats}
        activeKey={filters.status}
        activeScope={filters.scope}
        sortKey={sortKey}
        sortDir={sortDir}
        onSelectKpi={(v, isScope) => {
          if (isScope) {
            setFilters((f) => ({ ...f, scope: v || "", status: "", page: 1 }));
          } else {
            setFilters((f) => ({
              ...f,
              status: v || "",
              // Spec 2026-08-02 point #3 — cliquer sur un KPI de statut classique
              // doit désactiver un scope KPI actif (comme my_to_verify), sinon
              // les deux filtres se cumulent et le résultat est vide.
              scope: ["my_to_verify", "relance"].includes(f.scope) ? "" : f.scope,
              page: 1,
            }));
          }
        }}
        onKpiSecondClick={() => {
          // Spec iter128 pt.3 (Feb 2026) — 2e clic sur une carte KPI déjà
          // active : tri deadline DESC en plus du filtre. 3e clic : reset
          // du tri au défaut (created_at desc).
          if (sortKey === "deadline" && sortDir === "desc") {
            setSortKey("created_at");
            setSortDir("desc");
          } else {
            setSortKey("deadline");
            setSortDir("desc");
          }
        }}
        onExport={exportFile}
        canExport={can("taches", "export")}
        onCreate={() => setOpenCreate(true)}
        onOpenTrash={() => navigate("/taches/corbeille")}
        userRole={user?.role}
        isAdminLevel={isAdminLevel}
        canCreateTask={canCreateTask}
        viewScope={viewScope}
        onViewScopeChange={setViewScope}
        headerProfileSlot={<HeaderProfileGroup />}
      />

      <FiltersBar
        filters={filters}
        setFilters={setFilters}
        projects={projects.filter((p) => p.in_choices !== false)}
        users={users}
        isSearchDebouncing={
          filters.search !== debouncedSearch || filters.reference !== debouncedReference
        }
      />

      <Tabs value={view} onValueChange={guardTab(setView)} className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between flex-wrap gap-3 -mt-1">
          <OutlookTabs module="tasks" value={view} onChange={setView} testId="tasks-view-tabs" style={{ padding: 4, background: "#101013", border: "1px solid #26262b", borderRadius: 12 }}
            tabs={[{ key: "list", label: "Liste", icon: "list", testId: "tab-list" }, { key: "kanban", label: "Kanban", icon: "kanban", testId: "tab-kanban" }, { key: "calendar", label: "Calendrier", icon: "calendar", testId: "tab-calendar" }]} />
          {/* Spec rev92 (2026-08-02) — Groupe fusionné « Activité récente »
              + « Afficher les clôturées » (parité avec Documents rev90).
              Coins arrondis externes uniquement + séparateur vertical fin. */}
          {/* Spec iter266 point 5 (2026-02) — La case « Afficher les clôturées »
              a été retirée : redondante avec le KPI « Clôturée » en haut du
              tableau qui filtre déjà exclusivement sur ce statut. Le bouton
              « Activité récente » reste, avec des coins arrondis complets
              (plus de fusion). */}
          <div className="flex items-center" data-testid="tasks-toggle-group">
            <button
              type="button"
              onClick={() => {
                if (sortKey === "recent_activity") { setSortKey("created_at"); setSortDir("desc"); }
                else                                { setSortKey("recent_activity"); setSortDir("desc"); }
              }}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-l-md transition-colors"
              style={{
                background: sortKey === "recent_activity" ? "rgba(249,188,38,0.14)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${sortKey === "recent_activity" ? "rgba(249,188,38,0.55)" : "rgba(255,255,255,0.1)"}`,
                color: sortKey === "recent_activity" ? "#F9BC26" : "#EDEDED",
                fontWeight: 600,
              }}
              title="Trie les tâches par activité récente (dernier message, dernière checklist cochée, dernier événement)."
              data-testid="task-sort-recent-activity"
            >
              <Clock size={12} strokeWidth={2.4} />
              Activité récente
            </button>

          <span aria-hidden="true" style={{ width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.14)" }} />
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-1.5 rounded-r-md transition-colors"
            style={{ background: showCompleted ? "rgba(30,107,62,0.15)" : "rgba(255,255,255,0.03)", border: `1px solid ${showCompleted ? "var(--status-cloture-tasks)66" : "rgba(255,255,255,0.1)"}`, borderLeft: "none", color: showCompleted ? "var(--status-cloture-tasks)" : "#EDEDED" }}
            data-testid="tasks-toggle-show-completed">
            <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} className="w-3.5 h-3.5 accent-[var(--status-cloture-tasks)]" data-testid="tasks-toggle-show-completed-input" />
            Afficher les clôturées
          </label>
          </div>
        </div>

        <TabsContent value="list" className="mt-4 flex-1 min-h-0 flex flex-col">
          <TaskListView
            items={items}
            total={total}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMoreTasks}
            projects={projects}
            users={users}
            sortKey={sortKey}
            setSortKey={setSortKey}
            sortDir={sortDir}
            setSortDir={setSortDir}
          />
        </TabsContent>
        <TabsContent value="kanban" className="mt-4">
          <TaskKanbanView items={items} users={users} projects={projects} />
        </TabsContent>
        <TabsContent value="calendar" className="mt-4">
          <TaskCalendarView
            items={items}
            onCreateOnDate={(iso) => { setCreatePrefillDate(iso); setOpenCreate(true); }}
            initialMonth={monthFromUrl}
            onMonthChange={(ym) => {
              const params = new URLSearchParams(location.search);
              if (!ym) params.delete("month");
              else params.set("month", ym);
              const nextSearch = params.toString();
              const target = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
              if (target !== `${location.pathname}${location.search}`) {
                navigate(target, { replace: true });
              }
            }}
          />
        </TabsContent>
      </Tabs>

      <TaskCreateDialog
        open={openCreate}
        onOpenChange={(v) => { setOpenCreate(v); if (!v) setCreatePrefillDate(""); }}
        projects={projects.filter((p) => p.in_choices !== false)}
        users={users}
        onCreated={reload}
        initialExecutionDate={createPrefillDate}
      />

      {loading && (
        <div className="fixed bottom-6 left-32 text-xs text-muted-foreground bg-card border border-vertec-borderDark rounded-md px-3 py-1.5 animate-pulse">
          Chargement…
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// FusedTasksHeader — merged banner (icon + title + 6 mini-KPIs + actions).
// Spec 2026-02-14 : replaces the previous 2-row layout (title above / KPI
// cards below). One tidy strip that scans left→right.
// ---------------------------------------------------------------------------
const MINI_KPIS = [
  { key: "total",         label: "Total",             color: MODULE_COLORS.tasks, Icon: List,          filled: true,  isScope: false },
  { key: "planned",       label: "Planifiée",         color: "#378ADD",           Icon: CalendarClock, filled: false, isScope: false },
  { key: "waiting",       label: "En attente",        color: "#EF9F27",           Icon: Clock,         filled: false, isScope: false },
  { key: "in_progress",   label: "En cours",          color: "#D85A30",           Icon: Loader,        filled: false, isScope: false },
  { key: "late",          label: "En retard",         color: "#E24B4A",           Icon: AlertTriangle, filled: false, isScope: false },
  // iter402 pt 1g — « Relancé » : tâches relancées non terminées (indicateur superposé → scope, pas un statut).
  { key: "relance",       label: "Relancé",           color: "#E0569B",           Icon: BellRing,      filled: false, isScope: true },
  // Spec 2026-02-XX (Feb 2026) — nouvelle carte "Non vues" : tâches où
  // aucun exécuteur n'a encore ouvert la fiche. Couleur orange warning
  // (#EF9F27) qui reprend le pill escaladé de ReadStatusBadge au-delà de
  // 1h. Scope=unread côté backend. Sous-texte optionnel "dont N depuis +1h".
  { key: "unread",        label: "Non vues",          color: "#B983E8",           Icon: EyeOff,        filled: false, isScope: true,
    subtext: (s) => (s?.unread_overdue > 0 ? `dont ${s.unread_overdue} depuis +1h` : null) },
  // Spec 2026-08-02 point #2 — "À vérifier" renommé "Tous à vérifier".
  { key: "to_verify",     label: "Tous à vérifier",   color: "#8B5CF6",           Icon: Eye,           filled: false, isScope: false },
  // Spec 2026-08-02 point #3 — nouvelle carte "Mes à vérifier" côte-à-côte
  // avec "Tous à vérifier" (position b). Couleur violet plus foncé (#6D28D9,
  // Tailwind violet-700) — variante de var(--status-cloture-tasks) pour marquer la parenté
  // "sous-ensemble strict". Filtre backend via scope=my_to_verify.
  { key: "my_to_verify",  label: "À moi de vérifier",    color: "#6D28D9",           Icon: UserCheck,     filled: false, isScope: true },
  { key: "redo",          label: "À refaire",         color: "#D4537E",           Icon: RotateCw,      filled: false, isScope: false },
  // Spec iter123 pt.4 (Feb 2026) — carte KPI "Terminée" retirée
  // (statut phasé out, cf. iter122). "Clôturée" reste la carte finale.
  { key: "closed",        label: "Clôturée",          color: "var(--status-cloture-tasks)",           Icon: LockIcon,          filled: false, isScope: false },
];

function FusedTasksHeader({ total, stats, activeKey, activeScope, sortKey, sortDir, onSelectKpi, onKpiSecondClick, onExport, onCreate, onOpenTrash, userRole, isAdminLevel, canCreateTask, canExport = true, viewScope, onViewScopeChange, headerProfileSlot }) {
  const s = stats || {};
  const isGlobal = viewScope === "global";
  return (
    <div className="flex flex-col gap-3" data-testid="tasks-fused-header">
      {/* ============ LINE 1 — title + right actions ============ */}
      <div
        className="flex items-center gap-3"
        style={{
          background: "#1E1E1E",
          border: "1px solid #2A2A2A",
          borderRadius: 12,
          padding: "10px 16px",
        }}
      >
        {/* Left — icon + title + scope indicator */}
        <div className="flex items-center gap-2.5">
          <div
            className="rounded-lg flex items-center justify-center shrink-0"
            style={{ width: 32, height: 32, background: MODULE_COLORS.tasks }}
          >
            <ListTodo size={18} color="#1B1B1B" strokeWidth={2.4} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-base font-bold flex items-center gap-1.5">
              Tâches
              {/*
                Scope icon (spec 2026-02-18 sexies point 2a).
                • Vue perso → petit avatar `UserCircle2` (bleu Vertec).
                • Vue globale → globe (jaune module) — visible seulement
                  quand un admin a explicitement basculé.
              */}
              {isGlobal ? (
                <Globe2
                  size={14}
                  strokeWidth={2.2}
                  style={{ color: MODULE_COLORS.tasks }}
                  data-testid="tasks-scope-icon-global"
                >
                  <title>Vue globale — toutes les tâches de l'équipe</title>
                </Globe2>
              ) : (
                <UserCircle2
                  size={14}
                  strokeWidth={2.2}
                  style={{ color: "#3B9CE0" }}
                  data-testid="tasks-scope-icon-mine"
                >
                  <title>Vue personnelle — uniquement vos tâches</title>
                </UserCircle2>
              )}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {total} · {isGlobal ? "vue globale" : "vos tâches"}
            </span>
          </div>
        </div>

        <div className="flex-1" />

        {/* Right — Toggle "Vue globale" (admin only) + Corbeille */}
        <div className="flex items-center gap-2">
          <ScopeSwitch
            module="tasks"
            value={viewScope}
            onChange={(next) => onViewScopeChange?.(next)}
            testid="tasks-scope-toggle"
          />
          <HeaderPill
            icon={Trash2} iconBg="#EF4444" label="Corbeille"
            onClick={onOpenTrash}
            title="Corbeille" testid="tasks-trash-btn"
          />
          {/* iter410 L1-1 : export des tâches supprimé */}
        </div>

        {/* Visual gap then profile pill — floated to the very right */}
        {headerProfileSlot && (
          <div className="ml-3">
            {headerProfileSlot}
          </div>
        )}
      </div>

      {/* ============ LINE 2 — full-width KPI band + "+ Tâche" ============ */}
      <div
        className="flex items-stretch"
        style={{
          background: "#1E1E1E",
          border: "1px solid #2A2A2A",
          borderRadius: 12,
          padding: "10px 12px",
          gap: 6,
        }}
      >
        <div
          className="flex-1 grid"
          style={{
            // Spec iter127 pt.1 (Feb 2026) — répartition : TOTAL fixe 80 px,
            // 9 cartes de statut équi-réparties (1fr), « + TÂCHE » fixe 80 px.
            // Occupe toute la largeur, plus d'espace vide à droite.
            gridTemplateColumns: "80px repeat(10, minmax(0, 1fr)) 80px", // iter402 — +1 carte « Relancé »
            gap: 6,
          }}
          data-testid="tasks-mini-kpis"
        >
          {MINI_KPIS.map(({ key, label, color, Icon, filled, isScope, subtext }) => {
            const value = s[key] ?? 0;
            // Spec 2026-08-02 point #3 — pour un KPI scope, l'"active" se
            // détermine via filters.scope et non via filters.status (activeKey).
            const isActive = key === "total"
              ? !activeKey && !activeScope
              : (isScope ? activeScope === key : activeKey === key);
            const isTotal = key === "total";
            const handle = () => {
              if (isTotal) { onSelectKpi?.("", false); return; }
              // Spec iter141 (Feb 2026) — TOGGLE SIMPLE à 2 états (parité
              // avec le fix Documents iter133 pt.2). L'ancien cycle à 3
              // clics (iter128 pt.3, tri deadline DESC greffé sur le 2ᵉ
              // clic) créait une double coche verte perçue comme un état
              // intermédiaire trompeur — l'utilisateur devait cliquer une
              // 3ᵉ fois pour vraiment désélectionner. On revient au
              // comportement attendu : clic 1 sélectionne, clic 2 désé-
              // lectionne PROPREMENT (reset du filtre + reset du tri au
              // défaut).
              if (isActive) {
                onSelectKpi?.("", !!isScope);
                // Reset systématique du tri au défaut si un tri KPI-induit
                // était encore actif.
                if (sortKey === "deadline" && sortDir === "desc") {
                  onKpiSecondClick?.(); // signale le reset du tri
                }
                return;
              }
              onSelectKpi?.(key, !!isScope);
            };
            const cardStyle = isActive
              ? {
                  // Spec iter102 (Feb 2026) — KPI sélectionné : fond PLEIN
                  // couleur du module (yellow Tâches = MODULE_COLORS.tasks),
                  // texte blanc, pastille verte check top-right (via
                  // <KpiSelectedBadge/>). Non-actives conservent leur
                  // apparence sombre + accent gauche.
                  background: MODULE_COLORS.tasks,
                  border: "1px solid #FFFFFF66",
                  color: "#FFFFFF",
                  textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                }
              : (filled
                ? {
                    background: color,
                    border: "1px solid transparent",
                    color: "#FFFFFF",
                    textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                  }
                : {
                    background: "#18181a",
                    border: "1px solid #232326",
                    color: "#FFFFFF",
                  });
            return (
              <button
                type="button"
                key={key}
                onClick={handle}
                className="relative flex flex-col items-center justify-center rounded-md transition-all min-w-0"
                style={{
                  ...cardStyle,
                  padding: "6px 4px",
                  minHeight: 60,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => { if (!isActive && !filled) e.currentTarget.style.background = "#1c1c1e"; }}
                onMouseLeave={(e) => { if (!isActive && !filled) e.currentTarget.style.background = "#18181a"; }}
                data-testid={`tasks-mini-kpi-${key}`}
                title={label}
              >
                {isActive && !isTotal && <KpiSelectedBadge testId={`tasks-mini-kpi-${key}-selected`} />}
                {/* Spec iter129 pt.4 (Feb 2026) — chevron ▽ discret en
                    haut à droite quand la carte active a activé le tri
                    deadline desc (2e clic). */}
                {isActive && !isTotal && sortKey === "deadline" && sortDir === "desc" && (
                  <ChevronDown
                    size={12}
                    strokeWidth={2.6}
                    className="absolute top-0.5 right-0.5"
                    style={{ color: "#FFFFFF" }}
                    aria-label="Trié par échéance décroissante"
                    data-testid={`tasks-mini-kpi-${key}-sort-chevron`}
                  />
                )}
                {/* Spec 2026-02-18 decies point 1 — la carte Total a
                    maintenant une icône comme les 8 autres. */}
                <Icon size={13} strokeWidth={2.2} style={{ color: (isActive || filled) ? "#FFFFFF" : color }} />
                <span
                  className="font-bold tabular-nums"
                  style={{
                    fontSize: 21,
                    lineHeight: 1,
                    marginTop: 4,
                    color: "#FFFFFF",
                  }}
                >
                  {value}
                </span>
                {/*
                  Spec 2026-02-XX rev3 (Feb 2026) — libellé tronqué proprement
                  quand la carte est plus étroite que le texte (typiquement
                  ≤ 1200px de viewport pour "TOUS À VÉRIFIER" / "MES À VÉRIFIER").
                  `truncate` = overflow:hidden + text-overflow:ellipsis + nowrap.
                  `max-w-full` + `min-w-0` sur le parent garantissent que le
                  clipping se fait à la largeur de la carte, pas de la grille.
                  Tooltip natif sur le bouton (title={label}) expose déjà le
                  libellé complet.
                */}
                <span
                  className="uppercase mt-0.5 truncate max-w-full block text-center"
                  style={{ fontSize: 10, letterSpacing: "0.03em", color: (isActive || filled) ? "#FFFFFF" : "#9a9a95", fontWeight: 700 }}
                >
                  {label}
                </span>
                {/*
                  Spec 2026-02-XX (Feb 2026) — sous-texte optionnel affiché
                  UNIQUEMENT si le renderer `subtext(stats)` renvoie une
                  chaîne non vide. Utilisé par la carte "Non vues" pour
                  indiquer combien de tâches dépassent le seuil d'1h.
                */}
                {typeof subtext === "function" && subtext(s) ? (
                  <span
                    className="truncate max-w-full block text-center"
                    style={{
                      fontSize: 9,
                      lineHeight: 1.1,
                      marginTop: 2,
                      color: (isActive || filled) ? "#FFFFFF" : color,
                      fontWeight: 600,
                      opacity: 0.9,
                    }}
                    data-testid={`tasks-mini-kpi-${key}-subtext`}
                  >
                    {subtext(s)}
                  </span>
                ) : null}
              </button>
            );
          })}
          {/* Spec rev153 (2026-02-07) — bouton unifié via HeaderPill variant="kpi". */}
          <HeaderPill
            variant="kpi"
            iconBg={MODULE_COLORS.tasks}
            label="Tâche"
            onClick={onCreate}
            disabled={!canCreateTask}
            testid="tasks-new-btn"
            title={canCreateTask ? "Nouvelle tâche" : "Permission requise"}
          />
        </div>
      </div>
    </div>
  );
}
